import { useCallback, useEffect, useRef, useState } from 'react';
import {
  requestSoluu,
  SoluuError,
  type BleDebugEvent,
  type SoluuDevice,
  type WiFiStatus,
} from '@soluu/ble-core';

import { useAttemptIdRef } from '../state/AttemptIdContext.js';
import { useDiagnostics } from '../state/DiagnosticsProvider.js';

/** Connect lifecycle: idle → choosing (chooser open) → connecting (GATT handshake). */
export type ConnectPhase = 'idle' | 'choosing' | 'connecting';

interface State {
  device: SoluuDevice | null;
  status: WiFiStatus | null;
  phase: ConnectPhase;
  error: string | null;
}

/**
 * Owns the connected SoluuDevice and the live WiFiStatus stream. Subscribes
 * to status notifications on connect and unsubscribes on disconnect.
 *
 * Exposed at the App level (one device at a time per SPEC §"Out of Scope").
 */
export function useSoluuDevice() {
  const [state, setState] = useState<State>({
    device: null,
    status: null,
    phase: 'idle',
    error: null,
  });
  const cleanupRef = useRef<(() => void) | null>(null);
  const diagnostics = useDiagnostics();
  const attemptIdRef = useAttemptIdRef();
  // Stable callback that closes over refs, not state, so requestSoluu's
  // captured-once debug listener picks up the live attempt id.
  const debugRef = useRef<(event: BleDebugEvent) => void>(() => {});
  debugRef.current = (event: BleDebugEvent) => {
    diagnostics.pushEvent({
      attemptId: attemptIdRef.current,
      source: 'ble',
      type: event.type,
      payload: event,
    });
  };

  const wireDevice = useCallback((device: SoluuDevice) => {
    cleanupRef.current?.();
    const unsubStatus = device.onWiFiStatusChange((s) =>
      setState((prev) => ({ ...prev, status: s })),
    );
    const unsubDisc = device.onDisconnect(() => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      setState({ device: null, status: null, phase: 'idle', error: null });
    });
    cleanupRef.current = () => {
      unsubStatus();
      unsubDisc();
    };
    void device.readWiFiStatus().then(
      (s) => setState((prev) => ({ ...prev, status: s })),
      () => {
        /* a failed first read shouldn't crash the connect flow; the
         * notification stream will fill it in. */
      },
    );
  }, []);

  const connect = useCallback(async () => {
    setState((prev) => ({ ...prev, phase: 'choosing', error: null }));
    try {
      const device = await requestSoluu({
        debug: (event) => {
          // requestSoluu emits gatt:connecting once the chooser resolves and
          // the GATT handshake begins — the moment to stop saying "waiting
          // for chooser".
          if (event.type === 'gatt:connecting') {
            setState((prev) =>
              prev.phase === 'choosing' ? { ...prev, phase: 'connecting' } : prev,
            );
          }
          debugRef.current(event);
        },
      });
      wireDevice(device);
      setState({ device, status: null, phase: 'idle', error: null });
    } catch (cause) {
      const message =
        cause instanceof SoluuError
          ? `${cause.code}: ${cause.message}`
          : cause instanceof Error
            ? cause.message
            : 'Unknown error';
      setState({ device: null, status: null, phase: 'idle', error: message });
    }
  }, [wireDevice]);

  const disconnect = useCallback(async () => {
    const d = state.device;
    if (!d) return;
    await d.disconnect();
  }, [state.device]);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, []);

  return {
    device: state.device,
    status: state.status,
    phase: state.phase,
    connecting: state.phase !== 'idle',
    error: state.error,
    connect,
    disconnect,
  };
}
